import { alphaSort, alphaSorter } from 'vet-bones/utils';

import { ProjectPropertyType } from 'src/constants/project';
import { ModelConceptAttribute } from 'src/containers/ProjectDiagram/types/data';
import { ModelSnapshot } from 'src/containers/ProjectDiagram/types/model';
import { getInheritedAttributesByClass } from 'src/containers/ProjectDiagram/utils/getInheritedAttributesByClass';
import {
  OWL_DATATYPE_PROPERTY_IRI,
  RDFS_COMMENT_IRI,
  RDFS_LABEL_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import { isIriInProjectModels } from 'src/utils/isIriInProjectModels';
import { getPropertyIrisForModelConcept } from 'src/utils/stringify/getPropertyIrisForModelConcept';

const modelConceptAttributesSort = alphaSort((item: [string, any]) => item[0]);

export const stringifyModelConceptAttributes = ({
  classIris,
  filter,
  model,
  projectModelIds,
  projectPropertyType,
  targetModelId,
}: {
  classIris?: string[];
  filter?: (attribute: ModelConceptAttribute) => boolean;
  model: ModelSnapshot;
  projectModelIds?: Set<string>;
  projectPropertyType: ProjectPropertyType;
  targetModelId: string;
}): string => {
  const attributesByIri = { ...model.modelData.attributesByIri };

  if (classIris) {
    const inheritedAttributesByClass = getInheritedAttributesByClass(
      model,
      classIris
    );

    Object.keys(inheritedAttributesByClass).forEach((classIri) => {
      Object.keys(inheritedAttributesByClass[classIri]).forEach(
        (attributeKey) => {
          attributesByIri[attributeKey] = {
            ...attributesByIri[attributeKey],
            domains: {
              ...attributesByIri[attributeKey].domains,
              [classIri]: { value: classIri },
            },
          };
        }
      );
    });
  }

  let attributes = Object.entries(attributesByIri).filter(
    ([attributeIri]) =>
      !projectModelIds ||
      isIriInProjectModels(
        attributeIri,
        projectModelIds,
        model.modelData.modelsByIri,
        targetModelId
      )
  );
  if (filter) {
    attributes = attributes.filter(([, attribute]) => filter(attribute));
  }

  return attributes
    .sort(modelConceptAttributesSort)
    .map(([value, { domains, ranges }]) => {
      const lines = [`<${value}> a <${OWL_DATATYPE_PROPERTY_IRI}>`];

      const label = model.modelData.labelsByIri[value];
      if (label) {
        lines.push(`<${RDFS_LABEL_IRI}> "${escapeDoubleQuotes(label)}"`);
      }

      const description = model.modelData.descriptionsByIri[value];
      if (description) {
        lines.push(
          `<${RDFS_COMMENT_IRI}> "${escapeDoubleQuotes(description)}"`
        );
      }

      const { domainIri, rangeIri } = getPropertyIrisForModelConcept(
        projectPropertyType,
        model,
        value
      );

      const domainIris = Object.keys(domains);
      if (domainIris.length) {
        const objects = domainIris
          .map((object) => `<${object}>`)
          .sort(alphaSorter)
          .join(', ');
        lines.push(`<${domainIri}> ${objects}`);
      }

      const rangeIris = Object.keys(ranges);
      if (rangeIris.length) {
        const objects = rangeIris
          .map((object) => `<${object}>`)
          .sort(alphaSorter)
          .join(', ');
        lines.push(`<${rangeIri}> ${objects}`);
      }

      return `${lines.join(' ;\n  ')} .`;
    })
    .join('\n');
};
